import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaEye, FaTimes, FaBox, } from 'react-icons/fa';
import AssistantSidebar from '../components/AssistantSidebar';
import { API_BASE_URL } from '../config/api';
import '../styles/ManageAssetUsersPage.css';

const ManageAssetUsersPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAssets, setUserAssets] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [issuesMap, setIssuesMap] = useState({});

  useEffect(() => {
    if (user?.role !== 'asset_incharge') {
      navigate('/');
      return;
    }
    fetchAllAssetUsers();
  }, [user?.role, navigate]);

  const fetchAllAssetUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('token');
      
      // Fetch all users with asset_user role
      const response = await axios.get(`${API_BASE_URL}/api/users?role=asset_user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(response.data.users || []);
      } else {
        setUsers(response.data.users || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load asset users');
      console.error('Error fetching asset users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssets = async (userId) => {
    try {
      const token = sessionStorage.getItem('token');
      
      // Get user's assignment history
      const assignmentRes = await axios.get(
        `${API_BASE_URL}/api/asset-users/user-assignments/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (assignmentRes.data.success) {
      
        setUserAssets(assignmentRes.data.assignments || []);
        
        // Fetch issues for each assignment
        if (assignmentRes.data.assignments?.length > 0) {
          fetchIssuesForAssignments(assignmentRes.data.assignments, token);
        }
      }
    } catch (err) {
      console.error('Error fetching user assets:', err);
      setUserAssets([]);
    }
  };

  const fetchIssuesForAssignments = async (asgnts, token) => {
    try {
      const issuePromises = asgnts.map(asgnmt => {
        // Skip if no assetId or assetId._id
        if (!asgnmt.assetId || !asgnmt.assetId._id) {
          return Promise.resolve({ assignmentId: asgnmt._id, count: 0 });
        }
        
        return axios.get(`${API_BASE_URL}/api/issues?assetId=${asgnmt.assetId._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => ({
            assignmentId: asgnmt._id,
            count: res.data.issues?.length || 0
          }))
          .catch(() => ({ assignmentId: asgnmt._id, count: 0 }));
      });
      
      const issueResults = await Promise.all(issuePromises);
      const issueMap = {};
      issueResults.forEach(result => {
        issueMap[result.assignmentId] = result.count;
      });
      setIssuesMap(issueMap);
    } catch (err) {
      console.error('Error fetching issues:', err);
    }
  };

  const handleViewDetails = async (selectedUser) => {
    setSelectedUser(selectedUser);
    await fetchUserAssets(selectedUser._id);
    setShowDetailsModal(true);
  };

  const calculateDays = (fromDate, toDate) => {
    if (!fromDate) return 0;
    const from = new Date(fromDate);
    const to = toDate ? new Date(toDate) : new Date();
    const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const calculateRemainingDays = (assignedDate, returnDate, requestedDuration) => {
    // If already returned/released, show 0
    if (returnDate) {
      return '0';
    }

    // If no assigned date, return N/A
    if (!assignedDate) {
      return 'N/A';
    }

    // Parse duration string with multiple formats ("one_week", "1_week", "three_weeks", "3_weeks", "one_month", "1_month", etc.)
    const durationMap = {
      // Text format (one, two, three, etc.)
      'one_day': 1,
      'one_week': 7,
      'two_weeks': 14,
      'three_weeks': 21,
      'one_month': 30,
      'two_months': 60,
      'three_months': 90,
      'six_months': 180,
      // Numeric format (1_week, 2_weeks, 3_weeks, etc.)
      '1_day': 1,
      '1_week': 7,
      '2_weeks': 14,
      '3_weeks': 21,
      '1_month': 30,
      '2_months': 60,
      '3_months': 90,
      '6_months': 180
    };

    // If no duration specified, return N/A
    if (!requestedDuration) {
      console.log('Missing requestedDuration for assignment');
      return 'N/A';
    }

    const durationDays = durationMap[requestedDuration];
    
    // If duration not found in map, try to parse it intelligently
    if (!durationDays || durationDays === 0) {
      console.log('Unknown duration format:', requestedDuration);
      return 'N/A';
    }

    // Calculate expected release date
    const expectedReleaseDate = new Date(assignedDate);
    expectedReleaseDate.setDate(expectedReleaseDate.getDate() + durationDays);

    // Calculate days remaining from today to expected release date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expectedReleaseDate.setHours(0, 0, 0, 0);
    
    const remainingMs = expectedReleaseDate - today;
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    if (remainingDays < 0) {
      return 'Overdue';
    }
    
    return remainingDays === 0 ? 'Today' : `${remainingDays} days`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { label: 'Active', color: '#10b981' },
      'returned': { label: 'Released', color: '#8b5cf6' },
      'swapped': { label: 'Swapped', color: '#f59e0b' }
    };
    return badges[status] || { label: status, color: '#6b7280' };
  };

  if (loading) {
    return (
      <div className="manage-users-container">
        <AssistantSidebar user={user} />
        <div className="manage-users-content">
          <div className="loading">Loading asset users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-users-container">
      <AssistantSidebar user={user} />
      
      <div className="manage-users-content">
        {/* Header */}
        <div className="manage-header">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            <FaArrowLeft /> Back to Dashboard
          </button>
          <div>
            <h1>Manage Asset Users</h1>
            <p>View and manage all asset users and their allocations</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {users.length === 0 ? (
          <div className="empty-state">
            <FaBox className="empty-icon" />
            <h3>No asset users found</h3>
            <p>No users with asset_user role are registered yet.</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Total Assets</th>
                  <th>Active Assets</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr key={u._id} className="user-row">
                    <td className="row-number">{idx + 1}</td>
                    <td className="user-name">
                      <div className="name-info">
                        {u.profilePic && (
                          <img src={u.profilePic} alt={u.firstName} className="user-avatar" />
                        )}
                        <div className="name-text">
                          <strong>{u.firstName || 'N/A'} {u.lastName || ''}</strong>
                        </div>
                      </div>
                    </td>
                    <td className="email">{u.email || 'N/A'}</td>
                    <td className="department">{u.department || 'N/A'}</td>
                    <td className="total-assets">
                      <span className="badge">{u.totalAssets || 0}</span>
                    </td>
                    <td className="active-assets">
                      <span className="badge active">{u.activeAssets || 0}</span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-view-details"
                        onClick={() => handleViewDetails(u)}
                        title="View Details"
                      >
                        <FaEye /> View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="user-header-info">
                <h2>{selectedUser.firstName} {selectedUser.lastName}</h2>
                <p className="user-role">Asset User</p>
              </div>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* User Info Section */}
              <div className="info-section">
                <h4>User Information</h4>
                <div className="info-grid">
                  <div className="info-row">
                    <span className="label">Email:</span>
                    <span className="value">{selectedUser.email || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Department:</span>
                    <span className="value">{selectedUser.department || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Phone:</span>
                    <span className="value">{selectedUser.phone || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">User ID:</span>
                    <span className="value">{selectedUser._id}</span>
                  </div>
                </div>
              </div>

              {/* Assets Section */}
              <div className="info-section">
                <h4>Asset Allocations ({userAssets.length})</h4>
                
                {userAssets.length === 0 ? (
                  <div className="no-assets">
                    <p>No asset allocations for this user yet.</p>
                  </div>
                ) : (
                  <div className="assets-table-wrapper">
                    <table className="assets-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Asset Name</th>
                          <th>Serial Number</th>
                          <th>Unit ID</th>
                          <th>Assigned Date</th>
                          <th>Asset Usage Duration</th>
                          <th>Remaining Days</th>
                          <th>Status</th>
                          <th>Issues</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userAssets.map((assignment, idx) => {
                          const asset = assignment.assetId;
                          const statusBadge = getStatusBadge(assignment.status);
                          const issueCount = issuesMap[assignment._id] || 0;
                          
                          return (
                            <tr key={assignment._id}>
                              <td>{idx + 1}</td>
                              <td>{asset?.assetName || 'Unknown'}</td>
                              <td>{asset?.serialNumber || 'N/A'}</td>
                              <td>{assignment.unitId || 'Not specified'}</td>
                              <td>{formatDate(assignment.assignedDate)}</td>
                              <td>{calculateDays(assignment.assignedDate, assignment.returnDate)}</td>
                              <td className="remaining-days">
                                <span style={{
                                  color: calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration) === 'Overdue' ? '#dc2626' 
                                       : calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration) === '0' ? '#8b5cf6'
                                       : '#3b82f6',
                                  fontWeight: '600'
                                }}>
                                  {calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration)}
                                </span>
                              </td>
                              <td>
                                <span 
                                  className="status-badge-table"
                                  style={{ backgroundColor: statusBadge.color }}
                                >
                                  {statusBadge.label}
                                </span>
                              </td>
                              <td className={issueCount > 0 ? 'has-issues' : ''}>
                                {issueCount}
                              </td>
                              <td>{asset?.category || 'N/A'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAssetUsersPage;
